import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataGrid,
  PageHeader,
  TotalRow,
  type ColumnDef,
} from '@biovet/ui';
import {
  RECORD_SETS,
  type RecordColumn,
  type RecordRow,
  type RecordSet,
} from '../lib/design-data.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';

/**
 * Every register and report grid, from one component.
 *
 * The design draws twenty-odd of these and they differ only in their columns and
 * their rows — a deal register and a batch monitor are the same screen with
 * different headings. Writing twenty near-identical files would be twenty places
 * to fix the day the grid gains a date filter, so the difference is data
 * (`design-data.ts`) and the screen is one file.
 *
 * A screen that needs more than columns and rows — Deal Entry, Sales Invoice,
 * Import Costing — is not one of these and gets its own file.
 */

interface Normalized {
  cells: string[];
  head: boolean;
  bold: boolean;
  emphasis: boolean;
}

function normalize(row: RecordRow): Normalized {
  if (Array.isArray(row)) {
    return { cells: row, head: false, bold: false, emphasis: false };
  }
  return {
    cells: row.c,
    head: row.head ?? false,
    bold: row.bold ?? false,
    emphasis: row.emphasis ?? false,
  };
}

/**
 * A `RecordSet` as a printable, exportable report.
 *
 * Every register in the application is one of these, so this one function is
 * what gives all of them a print that is the register rather than a photograph
 * of the screen it is on — no nav rail, no filter chips, no stat tiles totalling
 * something the chips had filtered to, and no browser date and URL across the
 * page. It is also the export: `RecordSet` already carries the columns and the
 * rows, which is exactly what a spreadsheet needs.
 *
 * The `total` row is appended as a row rather than kept outside the table. On
 * screen it lives outside the scroller so virtualisation cannot eat it; on paper
 * and in a spreadsheet there is no scroller, and a total in the grid is one that
 * can be checked with a selection.
 */
export function reportOf(set: RecordSet, chipLabel?: string): ReportTable {
  return {
    title: set.title,
    fileName: set.title,
    meta: [chipLabel && chipLabel !== 'All' ? chipLabel : null, `${set.rows.length} rows`]
      .filter(Boolean)
      .join(' · '),
    columns: set.cols.map((c) => ({
      label: c.label,
      align: c.align === 'right' ? 'right' : 'left',
      width: c.width,
    })),
    rows: [
      ...set.rows.map((row) => {
        const r = normalize(row);
        return { cells: r.cells, head: r.head, total: r.bold || r.emphasis };
      }),
      ...(set.total ? [{ cells: set.total, total: true }] : []),
    ],
  };
}

export function RecordsScreen({ recordKey }: { recordKey: string }) {
  const set = RECORD_SETS[recordKey];
  if (!set) {
    // Reachable only if a nav key is added without a dataset. Loud rather than blank.
    return (
      <PageHeader
        title="No dataset for this screen"
        description={`\`${recordKey}\` is in the nav but has no entry in design-data.ts.`}
      />
    );
  }
  return <RecordGrid set={set} />;
}

export interface RecordGridProps {
  set: RecordSet;
  loading?: boolean;
  error?: string;
  /**
   * Which chip is lit, when the caller owns filtering.
   *
   * The chips used to be decoration — local state that nothing read. A screen
   * reading PostgreSQL filters its own rows, so it passes the index and the
   * setter and the chips become real. Left out, the old behaviour stands.
   */
  chip?: number;
  onChipChange?: (index: number) => void;
  /**
   * The register's own controls — a search box, `New shipment`, `Add product`.
   *
   * They are rendered **before** Print and Export XLSX, which every register
   * gets. This used to replace them, and the effect was that the fourteen
   * registers with a control of their own were the fourteen with no export at
   * all — and several had grown a hand-written `Print` button back, so the same
   * two buttons existed in two shapes with two behaviours. Print and export are
   * a property of being a register, not something each screen opts into.
   */
  actions?: ReactNode;
  /**
   * Withhold Print and Export.
   *
   * For a grid that is a panel inside a larger document rather than a register
   * in its own right — where the thing worth printing is the document above it.
   */
  noReport?: boolean;
  /** Extra content between the header and the grid — stat tiles, an open panel. */
  children?: ReactNode;
  /** Called with the index of the clicked row, into `set.rows`. */
  onRowSelect?: (index: number) => void;
  /** Index of the row to highlight, into `set.rows`. */
  selectedRow?: number | null;
  emptyTitle?: string;
  /**
   * Hide the register while something above it has the screen.
   *
   * Every one of these screens is a register with an entry panel that opens
   * above it, and opening the panel used to leave the register underneath —
   * so pressing `New shipment` gave you a form and, below it, four hundred rows
   * of the thing you had just decided not to read, with the page scrolled to
   * neither. Typing a document is a mode, and a mode should own the screen.
   *
   * The filter chips go with it, for the same reason: they filter a grid that
   * is not on screen, and a lit chip over a form invites the reading that the
   * form is filtered.
   *
   * The grid is *unmounted*, not hidden with a class — its selection, its
   * sort and its scroll position are all restored by the screen's own state
   * when the panel closes, and a `display:none` grid still virtualises rows
   * nobody can see.
   */
  hideGrid?: boolean;
}

/**
 * The grid chrome, for a `RecordSet` from anywhere.
 *
 * Screens that read PostgreSQL build their own `RecordSet` from the API's rows
 * and render it through here, so a live register and a sample one are the same
 * screen with a different source — one grid, one set of filter chips, one print
 * layout to keep right.
 */
export function RecordGrid({
  set,
  loading = false,
  error,
  chip,
  onChipChange,
  actions,
  children,
  onRowSelect,
  selectedRow,
  emptyTitle = 'Nothing in this register yet',
  hideGrid = false,
  noReport = false,
}: RecordGridProps) {
  const [localChip, setLocalChip] = useState(0);
  const activeChip = chip ?? localChip;
  const pickChip = onChipChange ?? setLocalChip;

  // The row index travels on the row itself: TanStack sorts the array, so the
  // position in `getRowModel()` is not the position in `set.rows`, and a click
  // on a sorted grid would otherwise open the wrong record.
  const rows = set.rows.map((row, index) => ({ ...normalize(row), index }));
  const columns = set.cols.map(toColumn);

  return (
    <>
      <PageHeader
        breadcrumb={set.crumb}
        title={set.title}
        description={set.subtitle}
        actions={
          <>
            {actions}
            {!noReport && (
              <>
                <Button size="sm" onClick={() => printReport(reportOf(set, set.chips[activeChip]))}>
                  Print
                </Button>
                {/* This button existed with no `onClick` at all on the registers
                    that reached it, and did not exist at all on the fourteen
                    that passed their own actions. It rendered, it was pressed,
                    and nothing happened. */}
                <Button
                  size="sm"
                  onClick={() => exportReport(reportOf(set, set.chips[activeChip]))}
                >
                  Export XLSX
                </Button>
              </>
            )}
          </>
        }
        meta={
          set.chips.length > 0 && !hideGrid ? (
            <div data-noprint="1" className="flex flex-wrap items-center gap-1.5">
              {set.chips.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => pickChip(i)}
                  aria-pressed={i === activeChip}
                  className={
                    i === activeChip
                      ? 'cursor-pointer rounded-[var(--radius-pill)] border border-transparent bg-[var(--accent-soft)] px-3 py-1 text-[12px] font-medium text-[var(--accent)]'
                      : 'cursor-pointer rounded-[var(--radius-pill)] border border-[var(--line)] bg-[var(--surface)] px-3 py-1 text-[12px] text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:text-[var(--ink)]'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      {error && (
        <Callout tone="danger" title="This report could not be loaded">
          {error}
        </Callout>
      )}

      {children}

      {hideGrid ? null : (
        <Card flush data-rpt="1">
          <DataGrid<Normalized & { index: number }>
            data={rows}
            columns={columns}
            loading={loading}
            emptyTitle={emptyTitle}
            aria-label={set.title}
            onRowClick={onRowSelect ? (row) => onRowSelect(row.index) : undefined}
            rowClassName={(row) =>
              [
                row.head && 'bg-[var(--surface-2)] font-medium',
                row.bold && !row.emphasis && 'font-semibold',
                row.emphasis && 'bg-[var(--surface-2)] font-semibold',
                selectedRow === row.index && 'bg-[var(--accent-soft)]',
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            footer={
              set.total ? (
                <TotalRow
                  cells={set.total.map((content, i) => ({
                    content,
                    align: set.cols[i]?.align,
                    numeric: set.cols[i]?.mono,
                    width: set.cols[i]?.width,
                  }))}
                />
              ) : undefined
            }
          />
        </Card>
      )}
    </>
  );
}

/**
 * One column definition per design column.
 *
 * `accessorFn` reads by index rather than by key because the datasets are
 * positional — the design's rows are arrays, and giving each one a bespoke
 * object shape would mean a bespoke type per grid for no gain.
 */
function toColumn(
  col: RecordColumn,
  index: number,
): ColumnDef<Normalized & { index: number }, string> {
  return {
    id: String(index),
    header: col.label,
    accessorFn: (row) => row.cells[index] ?? '',
    meta: { align: col.align, numeric: col.mono, width: col.width },
    cell: (ctx) => {
      const value = ctx.getValue();
      // A section header spans its row: 'Revenue', 'Zone North'. Only the first
      // cell carries text, and badging or right-aligning it would be noise.
      if (ctx.row.original.head) return index === 0 ? value : null;
      if (!value || value === '—')
        return <span className="text-[var(--ink-3)]">{value || '—'}</span>;
      if (col.badge) {
        // Ageing cells are durations, not statuses — '23 days' will never be in
        // `STATUS_TONE` and should not be. Sixty days is where the design's own
        // ageing report stops calling it a delay and starts calling it a problem.
        const days = /^(\d+) days$/.exec(value);
        if (days) {
          return <Badge tone={Number(days[1]) >= 60 ? 'bad' : 'warn'}>{value}</Badge>;
        }
        // Tone resolves centrally from `STATUS_TONE`; the label stays the
        // design's own wording rather than `humanizeStatus`'s sentence case, so
        // 'In Transit' does not become 'In transit' on one screen out of six.
        return <Badge status={toStatusEnum(value)}>{value}</Badge>;
      }
      return value;
    },
  };
}

/** `'Under Clearance'` → `'UNDER_CLEARANCE'`, the shape `STATUS_TONE` keys on. */
function toStatusEnum(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}
